import React, { useState, useEffect } from 'react';
import { AlertCircle, DollarSign, Calendar, Download, Search, Filter } from 'lucide-react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const FineReport = () => {
  const [filters, setFilters] = useState({ 
    from_date: '', 
    to_date: '',
    course_id: '',
    semester_id: '',
    section_id: ''
  });
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [sections, setSections] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, paid, pending

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchFiltersData();
  }, []);

  const fetchFiltersData = async () => {
    try {
      const [coursesRes, semestersRes, sectionsRes] = await Promise.all([
        axios.get(`${API_URL}/api/master-settings/courses`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/master-settings/semesters`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/master-settings/sections`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCourses(coursesRes.data);
      setSemesters(semestersRes.data);
      setSections(sectionsRes.data);
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  const fetchReport = async () => {
    if (!filters.from_date || !filters.to_date) {
      alert('Please select both From Date and To Date');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);
      if (filters.course_id) params.append('course_id', filters.course_id);
      if (filters.semester_id) params.append('semester_id', filters.semester_id);
      if (filters.section_id) params.append('section_id', filters.section_id);

      const response = await axios.get(`${API_URL}/api/attendance/fine-report?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      alert('Error fetching report data');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    if (!reportData || !reportData.fine_details) return;

    const data = filteredFineDetails.map(item => ({
      'Admission No': item.admission_number,
      'Student Name': item.student_name,
      'Course': item.course_name,
      'Semester': item.semester,
      'Section': item.section,
      'Total Classes': item.total_classes,
      'Present': item.present_count,
      'Attendance %': item.attendance_percentage,
      'Fine Amount': item.fine_amount,
      'Status': item.status,
      'Reason': item.fine_reason
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Fine Report');
    XLSX.writeFile(wb, `Fine_Report_${filters.from_date}_to_${filters.to_date}.xlsx`);
  };

  const filteredFineDetails = reportData?.fine_details?.filter(item => {
    const matchesSearch = item.student_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.admission_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'paid' && item.is_paid) ||
                         (statusFilter === 'pending' && !item.is_paid);
    return matchesSearch && matchesStatus;
  }) || [];

  const stats = reportData?.statistics || {
    total_fines: 0,
    students_with_fines: 0,
    average_fine: 0
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Fine Report</h1>
          <p className="text-sm text-gray-500 mt-1">Track attendance-based fines</p>
        </div>
        {reportData && (
          <button 
            onClick={exportToExcel}
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          >
            <Download className="h-4 w-4" />
            Export to Excel
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-red-500 to-red-600 p-6 rounded-lg shadow-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-100">Total Pending Fines</p>
              <p className="text-3xl font-bold mt-1">₹{stats.total_fines}</p>
            </div>
            <AlertCircle className="h-12 w-12 text-red-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 rounded-lg shadow-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-100">Students with Fines</p>
              <p className="text-3xl font-bold mt-1">{stats.students_with_fines}</p>
            </div>
            <DollarSign className="h-12 w-12 text-orange-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-6 rounded-lg shadow-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-100">Average Fine</p>
              <p className="text-3xl font-bold mt-1">₹{stats.average_fine}</p>
            </div>
            <Calendar className="h-12 w-12 text-purple-200" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-5 w-5 text-gray-500" />
          <h3 className="text-lg font-semibold">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">From Date *</label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.from_date}
              onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">To Date *</label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.to_date}
              onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Course</label>
            <select
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.course_id}
              onChange={(e) => setFilters({ ...filters, course_id: e.target.value })}
            >
              <option value="">All Courses</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Semester</label>
            <select
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.semester_id}
              onChange={(e) => setFilters({ ...filters, semester_id: e.target.value })}
            >
              <option value="">All Semesters</option>
              {semesters.map(semester => (
                <option key={semester.id} value={semester.id}>{semester.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Section</label>
            <select
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.section_id}
              onChange={(e) => setFilters({ ...filters, section_id: e.target.value })}
            >
              <option value="">All Sections</option>
              {sections.map(section => (
                <option key={section.id} value={section.id}>{section.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4">
          <button 
            onClick={fetchReport}
            disabled={loading}
            className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Loading...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {reportData && (
        <>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex gap-4 items-center mb-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or admission number..."
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <select
                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
              </select>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admission No</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attendance</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fine Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredFineDetails.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{item.admission_number}</td>
                      <td className="px-4 py-3 text-sm font-medium">{item.student_name}</td>
                      <td className="px-4 py-3 text-sm">{item.course_name}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`${item.attendance_percentage < 60 ? 'text-red-600' : item.attendance_percentage < 75 ? 'text-orange-600' : 'text-green-600'} font-semibold`}>
                          {item.attendance_percentage}%
                        </span>
                        <span className="text-gray-500 text-xs ml-1">
                          ({item.present_count}/{item.total_classes})
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-red-600">₹{item.fine_amount}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{item.fine_reason}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          item.is_paid ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                        }`}>
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredFineDetails.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No fine data available for the selected filters
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {!reportData && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Select date range and click "Generate Report" to view fine data</p>
        </div>
      )}
    </div>
  );
};

export default FineReport;